import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Alert,
  Tabs,
  Tab,
  Button
} from '@mui/material';
import {
  TrendingUp,
  Receipt,
  Inventory,
  TrendingDown,
  ShowChart,
  PointOfSale,
  Add,
  WineBar,
  ArrowForward
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import statsService from '../services/statsService';
import productService from '../services/productService';
import saleService from '../services/saleService';
import './Dashboard.css';

const StatCard = ({ title, value, subtitle, icon, color, trend }) => (
  <Card className="dashboard-stat-card" sx={{ '--stat-color': color }}>
    <CardContent className="dashboard-stat-content">
      <Box className="d-flex justify-between align-center">
        <Box className="flex-1">
          <Typography className="dashboard-stat-label" variant="body2">
            {title}
          </Typography>
          <Typography variant="h5" className="dashboard-stat-value">
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
          {trend !== undefined && trend !== null && (
            <Box className="d-flex align-center gap-xs mt-xs">
              {trend >= 0 ? (
                <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />
              ) : (
                <TrendingDown sx={{ fontSize: 16, color: 'error.main' }} />
              )}
              <Typography 
                variant="caption" 
                sx={{ color: trend >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}
              >
                {trend >= 0 ? '+' : ''}{trend.toFixed(1)}%
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          className="dashboard-stat-icon"
          sx={{
            backgroundColor: `${color}20`,
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 25, color } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [recentSales, setRecentSales] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Cargar dashboard completo con estadísticas
      const dashboardResult = await statsService.getDashboard();
      if (dashboardResult.success) {
        setDashboardData(dashboardResult.data);
      } else {
        setError(dashboardResult.message);
      }

      // Cargar productos con bajo stock
      const productsResult = await productService.getAll({ lowStock: true });
      if (productsResult.success) {
        setLowStockProducts(productsResult.data);
      }

      const salesResult = await saleService.getAll({ page: 1, limit: 5 });
      if (salesResult.success) {
        setRecentSales(salesResult.data || []);
      }
    } catch (err) {
      setError('Error al cargar datos del dashboard');
      console.error(err);
    }

    setLoading(false);
  };

  if (loading) {
    return (
      <Box className="d-flex justify-center align-center min-h-60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" className="mb-lg font-bold">
          Dashboard
        </Typography>
        <Alert severity="error" className="mb-md">
          {error}
        </Alert>
      </Box>
    );
  }

  if (!dashboardData) {
    return (
      <Box>
        <Typography variant="h4" className="mb-lg font-bold">
          Dashboard
        </Typography>
        <Alert severity="info">
          No hay datos disponibles en este momento.
        </Alert>
      </Box>
    );
  }

  const renderPeriodStats = (period, title) => {
    if (!period) return null;

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6" className="font-semibold mb-sm">
            {title}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="VENTAS"
            value={`$${period.ventas.toLocaleString('es-AR')}`}
            subtitle={`${period.cantidadVentas} transacciones`}
            icon={<Receipt />}
            color="#315d9e"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="COSTOS"
            value={`$${period.costos.toLocaleString('es-AR')}`}
            icon={<TrendingDown />}
            color="#c74d4d"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="GANANCIA"
            value={`$${period.ganancia.toLocaleString('es-AR')}`}
            icon={<TrendingUp />}
            color="#4f8a57"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="MARGEN"
            value={`${period.margen.toFixed(2)}%`}
            icon={<ShowChart />}
            color="#b77b24"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="STOCK BAJO"
            value={lowStockProducts.length}
            icon={<Inventory />}
            color="#7a1238"
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <Box className="wine-dashboard">
      <Box className="dashboard-heading">
        <Box>
          <Typography className="dashboard-kicker">RESUMEN COMERCIAL</Typography>
          <Typography variant="h4">Ventas y rentabilidad</Typography>
          <Typography className="dashboard-subtitle">Una vista rápida del movimiento de tu vinoteca.</Typography>
        </Box>
        <Chip 
          label="Actualizado ahora" 
          className="dashboard-updated-chip"
          size="small"
          icon={<TrendingUp />}
        />
      </Box>

      {/* Tabs de períodos */}
      <Card className="dashboard-period-card">
        <Tabs 
          value={currentTab} 
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Hoy" />
          <Tab label="Esta Semana" />
          <Tab label="Este Mes" />
          <Tab label="Este Año" />
        </Tabs>
      </Card>

      {/* Estadísticas según el tab seleccionado */}
      <Box className="dashboard-stats-section">
        {currentTab === 0 && renderPeriodStats(dashboardData.hoy, 'Estadísticas de Hoy')}
        {currentTab === 1 && renderPeriodStats(dashboardData.estaSemana, 'Estadísticas de Esta Semana')}
        {currentTab === 2 && renderPeriodStats(dashboardData.esteMes, 'Estadísticas de Este Mes')}
        {currentTab === 3 && renderPeriodStats(dashboardData.esteAño, 'Estadísticas de Este Año')}
      </Box>

      <Grid container spacing={2} className="dashboard-workspace">
        <Grid item xs={12} lg={8}>
          <Card className="dashboard-panel-card">
            <CardContent>
              <Box className="dashboard-panel-heading">
                <Box>
                  <Typography className="dashboard-panel-kicker">ACTIVIDAD</Typography>
                  <Typography variant="h6">Ventas recientes</Typography>
                </Box>
                <Button endIcon={<ArrowForward />} onClick={() => navigate('/sales')}>Ver todas</Button>
              </Box>
              {recentSales.length === 0 ? (
                <Box className="dashboard-empty-state">
                  <WineBar />
                  <Typography variant="h6">Todavía no hay ventas registradas</Typography>
                  <Typography>Cuando realices la primera venta, aparecerá aquí con su importe y forma de pago.</Typography>
                  <Button variant="contained" startIcon={<PointOfSale />} onClick={() => navigate('/pos')}>Registrar primera venta</Button>
                </Box>
              ) : (
                <Box className="dashboard-recent-list">
                  {recentSales.map((sale) => (
                    <Box className="dashboard-recent-row" key={sale.id}>
                      <Box>
                        <Typography className="dashboard-sale-number">{sale.saleNumber}</Typography>
                        <Typography className="dashboard-sale-meta">
                          {sale.customerName || 'Consumidor final'} · {new Date(sale.saleDate).toLocaleDateString('es-AR')}
                        </Typography>
                      </Box>
                      <Typography className="dashboard-sale-total">${parseFloat(sale.total || 0).toLocaleString('es-AR')}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card className="dashboard-panel-card dashboard-actions-card">
            <CardContent>
              <Typography className="dashboard-panel-kicker">ACCESOS RÁPIDOS</Typography>
              <Typography variant="h6">¿Qué querés hacer?</Typography>
              <Button variant="contained" startIcon={<PointOfSale />} onClick={() => navigate('/pos')}>Nueva venta</Button>
              <Button variant="outlined" startIcon={<Add />} onClick={() => navigate('/products')}>Agregar etiqueta</Button>
              <Button variant="outlined" startIcon={<Inventory />} onClick={() => navigate('/products')}>Revisar inventario</Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top Productos del Mes */}
      {dashboardData.topProductosDelMes && dashboardData.topProductosDelMes.length > 0 && (
        <Card className="mb-lg">
          <CardContent>
            <Typography variant="h6" className="mb-md font-semibold">
              Top Productos del Mes
            </Typography>
            <Box className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Código</th>
                    <th className="text-center">Cantidad</th>
                    <th className="text-right">Ventas</th>
                    <th className="text-right">Ganancia</th>
                    <th className="text-center">Margen</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.topProductosDelMes.map((product) => (
                    <tr key={product.id}>
                      <td className="font-semibold">{product.nombre}</td>
                      <td className="font-mono">{product.codigo}</td>
                      <td className="text-center">{product.cantidadVendida}</td>
                      <td className="text-right font-semibold">
                        ${parseFloat(product.ventas || 0).toLocaleString('es-AR')}
                      </td>
                      <td className="text-right text-profit">
                        ${parseFloat(product.ganancia || 0).toLocaleString('es-AR')}
                      </td>
                      <td className="text-center">
                        <Chip 
                          label={`${((product.ganancia / product.ventas) * 100 || 0).toFixed(1)}%`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Productos con Stock Bajo */}
      {lowStockProducts.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" className="mb-md font-semibold">
              <Box className="d-flex align-center gap-sm">
                <Inventory />
                Productos con Stock Bajo
                <Chip 
                  label={lowStockProducts.length} 
                  size="small" 
                  color="error"
                />
              </Box>
            </Typography>
            <Box className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th className="text-center">Stock Actual</th>
                    <th className="text-center">Stock Mínimo</th>
                    <th className="text-center">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {lowStockProducts.map((product) => (
                    <tr key={product.id}>
                      <td className="font-semibold">{product.name}</td>
                      <td className="text-center">{product.stock}</td>
                      <td className="text-center">{product.minStock}</td>
                      <td className="text-center">
                        <Chip 
                          label={product.stock === 0 ? 'Sin Stock' : 'Stock Bajo'}
                          size="small"
                          color={product.stock === 0 ? 'error' : 'warning'}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default Dashboard;


